import type { LeadStage } from '@gty/common';

/**
 * Indian-locale formatting.
 *
 * `en-IN` groups in lakhs and crores (1,12,000 — not 112,000), which is what
 * every figure in the design uses. Formatters are module-level constants
 * because constructing an `Intl.NumberFormat` is not free and these run inside
 * table rows.
 */
/**
 * `minimumFractionDigits: 0` with `maximumFractionDigits: 2` is the "no
 * decimals unless the value has paise" rule itself, not a workaround for
 * it — `Intl` only prints the fractional part a number actually has.
 */
const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const COUNT = new Intl.NumberFormat('en-IN');

/**
 * The ONE shared money formatter (item 4) — full, grouped (en-IN lakh/crore
 * digit grouping, e.g. ₹1,12,000), never abbreviated to "K"/"L"/"Cr". Every
 * screen that used to call the retired `abbreviateCurrency` now calls this
 * instead, so a figure reads the same everywhere it appears.
 */
export function formatCurrency(value: number): string {
  return INR.format(value);
}

export function formatCount(value: number): string {
  return COUNT.format(value);
}

/**
 * A file size a person can read — "480 KB", "2.3 MB", "0 bytes".
 *
 * BINARY units (1 KB = 1024 bytes) because every cap it describes is written
 * that way in code — `512 * 1024`, `8 * 1024 * 1024` — and a decimal formatter
 * would tell someone their 8,300,000-byte file is "8.3 MB" while the server
 * refuses it for being over an 8 MB limit that is really 8,388,608. The number
 * on screen has to be the number the limit is measured in, or the refusal reads
 * as a bug.
 *
 * NOT `Intl` — there is no locale-aware byte format, and the lakh/crore
 * grouping the rest of this file exists for is actively wrong for file sizes.
 *
 * Lives here rather than beside either caller because two upload fields now
 * quote sizes at users (the invoice payment QR and the tour brochure), and a
 * second copy of this is a second set of strings that can round differently.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** "Saturday, 1 August 2026" — the dashboard's date line. */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** "27 Jul 2026". */
export function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "27 Jul 2026, 2:30 PM" — a REAL timestamp (audit trail, booking notes, lead
 * activity, document edit history), not the voucher's naive wall-clock
 * strings (`formatWallClock` in `VoucherDocument.tsx` handles those — text in,
 * text out, no zone conversion, because they never had one to convert).
 *
 * `timeZone: 'Asia/Kolkata'` is explicit rather than left to the browser's own
 * system clock: every one of these timestamps is stored in UTC and means
 * nothing to staff until it is placed in the one zone the business operates
 * in, regardless of what zone the viewing machine happens to be set to.
 * `hour12: true` is explicit for the same reason `formatWallClock` spells out
 * AM/PM rather than leaving it to a locale default — this is a product
 * decision, not an accident of whatever `en-IN` happens to default to today.
 *
 * UPPERCASED ON PURPOSE. `Intl`'s own `en-IN` output spells it "pm"/"am"
 * lowercase, which would read as a mismatch next to `WallClockPicker`'s and
 * `formatWallClock`'s own "AM"/"PM" elsewhere in the same app.
 */
export function formatDateTime(iso: string): string {
  return new Date(iso)
    .toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    })
    .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

/** "2:30 PM" — the time half of `formatDateTime`, for a row under a day heading that already names the date. */
export function formatTimeOnly(iso: string): string {
  return new Date(iso)
    .toLocaleString('en-IN', { timeStyle: 'short', hour12: true, timeZone: 'Asia/Kolkata' })
    .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

/**
 * Split an ISO date into the day/month pair the departures list stacks.
 * Parsed as UTC so a date-only string does not shift a day in +05:30.
 */
export function dayAndMonth(iso: string): { day: string; month: string } {
  const date = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return {
    day: String(date.getUTCDate()).padStart(2, '0'),
    month: date.toLocaleDateString('en-IN', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
  };
}

/** Lead stage pill styling, matching the design's board columns. */
export const LEAD_STAGE_LABEL: Readonly<Record<LeadStage, string>> = Object.freeze({
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUOTED: 'Quoted',
  CONVERTED: 'Converted',
  LOST: 'Lost',
});

export const LEAD_STAGE_PILL: Readonly<Record<LeadStage, string>> = Object.freeze({
  NEW: 'bg-brand/10 text-brand',
  CONTACTED: 'bg-info-bg text-info',
  QUOTED: 'bg-warning/16 text-warning-ink',
  CONVERTED: 'bg-success-bg text-success-ink',
  LOST: 'bg-neutral-200 text-neutral-600',
});

/** Two-letter avatar initials. Returns '?' rather than an empty circle. */
export function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter((p) => p.length > 0);
  const initials = parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
  return initials.length > 0 ? initials : '?';
}
